import React, { useMemo, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import Tracking from '@spotahome/soyuz-tracking';
import { Mediator } from '@spotahome/soyuz-mediator';

import { useHeaderLinks } from '../../context/ExternalLink';

import MenuIconMobile from '../MenuIconMobile';
import Logo from '../../../base/Logo';
import Modal from '../Modal';

import { COMMON_LOGIN_MODAL } from '../utils/constants';

import Header from './Header/Header';
import HeaderNav from './HeaderNav';
import HeaderNavMobile from './HeaderNav/HeaderNavMobile';

import linkShape from './shapes/link.shape';
import loginShape from './shapes/login.shape';
import themeShape, { DARK_THEME, LIGHT_THEME } from './shapes/theme.shape';

const HeaderContainer = ({
  links,
  color,
  transparent,
  locales,
  login,
  showMobileMenu,
  onAppClick,
  renderLeftContent,
  renderExtraContent,
  isTopTenantVariant,
  isContactUsEnabled,
  isTenantLogged,
  ...restProps
}) => {
  const headerLinks = useHeaderLinks();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(open => !open);
  const closeMenu = () => setMenuOpen(false);
  const { logo: logoLink = {}, ...otherLinks } = links;

  const { backgroundColor, foregroundColor, logoColor } = useMemo(
    () => ({
      backgroundColor: transparent ? 'transparent' : color.background || color,
      foregroundColor:
        color.foreground || color === DARK_THEME ? LIGHT_THEME : DARK_THEME,
      logoColor:
        color.logotype || color === DARK_THEME ? LIGHT_THEME : DARK_THEME
    }),
    [transparent, color]
  );

  useEffect(() => {
    Mediator.subscribe(COMMON_LOGIN_MODAL, closeMenu);
    return () => {
      Mediator.unsubscribe(COMMON_LOGIN_MODAL, closeMenu);
    };
  }, []);

  const handleAppStoreClick = () => onAppClick();

  const handleOpenMobileNavClick = () =>
    Tracking.ga.sendEvent('MobileNavigationBar', 'Menu', 'Open');

  const renderHeader = () => (
    <Header
      logo={
        <a className="ga-menu-home" href={headerLinks.landing} {...logoLink}>
          <Logo theme={logoColor} size="normal" />
        </a>
      }
      menuIcon={
        !!showMobileMenu && (
          <MenuIconMobile
            color={foregroundColor}
            onMenuClick={toggleMenu}
            isTopTenantVariant={isTopTenantVariant}
            isTenantLogged={isTenantLogged}
          />
        )
      }
      {...restProps}
      color={backgroundColor}
      renderLeftContent={renderLeftContent}
      renderExtraContent={renderExtraContent}
    >
      <HeaderNav
        {...otherLinks}
        isTransparent={transparent}
        locales={locales}
        color={foregroundColor}
        login={login}
        isTopTenantVariant={isTopTenantVariant}
      />
    </Header>
  );

  return (
    <Fragment>
      {renderHeader()}
      {menuOpen && (
        <Modal
          contentLabel="modal"
          size="fullscreen"
          isOpen={menuOpen}
          onAfterOpen={handleOpenMobileNavClick}
        >
          <HeaderNavMobile
            {...links}
            onAppClick={handleAppStoreClick}
            locales={locales}
            login={login}
            onClose={toggleMenu}
            isTopTenantVariant={isTopTenantVariant}
            isContactUsEnabled={isContactUsEnabled}
          />
        </Modal>
      )}
    </Fragment>
  );
};

HeaderContainer.propTypes = {
  transparent: PropTypes.bool,
  color: PropTypes.oneOfType([
    themeShape,
    PropTypes.shape({
      background: themeShape,
      foreground: themeShape,
      logotype: themeShape
    })
  ]),
  links: PropTypes.shape({
    logo: PropTypes.shape(linkShape)
  }),
  locales: PropTypes.shape({
    current: PropTypes.string,
    list: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func
  }),
  login: PropTypes.shape(loginShape),
  showMobileMenu: PropTypes.bool,
  onAppClick: PropTypes.func,
  renderLeftContent: PropTypes.func,
  renderExtraContent: PropTypes.func,
  isTopTenantVariant: PropTypes.bool,
  isTenantLogged: PropTypes.bool,
  isContactUsEnabled: PropTypes.bool
};

HeaderContainer.defaultProps = {
  transparent: false,
  color: LIGHT_THEME,
  links: {},
  login: {},
  locales: {
    current: 'en'
  },
  showMobileMenu: true,
  onAppClick: () => {},
  renderLeftContent: () => {},
  renderExtraContent: () => {},
  isTopTenantVariant: false,
  isTenantLogged: false,
  isContactUsEnabled: false
};

export default HeaderContainer;
